import { memo, ReactNode } from 'react'
import { Handle, Position } from 'reactflow'
import clsx from 'clsx'

interface BaseNodeProps {
  title: string
  icon?: ReactNode
  children?: ReactNode
  color?: string
  selected?: boolean
  hasInput?: boolean
  hasOutput?: boolean
}

export const BaseNode = memo(
  ({
    title,
    icon,
    children,
    color = 'blue',
    selected = false,
    hasInput = true,
    hasOutput = true,
  }: BaseNodeProps) => {
    const colorClasses = {
      blue: 'border-blue-500 bg-blue-500/10',
      green: 'border-green-500 bg-green-500/10',
      purple: 'border-purple-500 bg-purple-500/10',
      orange: 'border-orange-500 bg-orange-500/10',
      red: 'border-red-500 bg-red-500/10',
      gray: 'border-gray-500 bg-gray-50',
    }

    return (
      <div
        className={clsx(
          'min-w-[200px] rounded-lg border-2 bg-white shadow-md transition-all',
          selected ? 'ring-2 ring-primary-500 ring-offset-2' : '',
          colorClasses[color as keyof typeof colorClasses] || colorClasses.blue
        )}
      >
        {hasInput && (
          <Handle
            type="target"
            position={Position.Top}
            className="!bg-gray-400 !border-2 !border-white"
          />
        )}

        <div className="p-3">
          <div className="flex items-center gap-2 mb-2">
            {icon && <div className="text-gray-700">{icon}</div>}
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          </div>
          {children && <div className="text-xs text-gray-600">{children}</div>}
        </div>

        {hasOutput && (
          <Handle
            type="source"
            position={Position.Bottom}
            className="!bg-gray-400 !border-2 !border-white"
          />
        )}
      </div>
    )
  }
)

BaseNode.displayName = 'BaseNode'
